<template>
  <div class="m-5 pb-5">
    <ButtonPanel :buttonIcon="'refresh'" :click="refresh" icon="history" :title="`${$t(`message.submissionHistory`)}`">
      <div v-if="sortedHistoryList.length > 0">
        <HistoryListHeader />
        <div v-for="history in sortedHistoryList" :key="history.judgeId">
          <div v-if="hasDataInExpandedList(history.judgeId).value && hasDataInLoadingList(history.judgeId).value">
            <NestedElement
              :isSummaryExpanded="historyExpandedList[history.judgeId]"
              :loading="historyLoadingList[history.judgeId]"
              @clickSummary="clickSummary(history.judgeId)"
            >
              <template #labels>
                <div :class="`w-full grid grid-cols-8`">
                  <div class="px-2 py-3 my-auto text-center col-span-2">
                    {{ history.judgeId }}
                  </div>
                  <div class="px-2 py-3 my-auto text-center flex items-center justify-center">
                    <JudgeBadge :status="history.status" />
                  </div>
                  <div class="px-2 py-3 my-auto text-center col-span-2"> {{ history.programLanguage }} </div>
                  <div class="px-2 py-3 my-auto text-center col-span-3">
                    {{ timestampToDate(Number(history.submissionDate)) }}
                  </div>
                </div>
              </template>
              <template v-slot:body>
                <div v-if="historySourceCodeList[history.judgeId]">
                  <div class="px-3 py-3" style="height: 400px">
                    <MonacoEditorRead
                      class="h-full border dark:border-dark-400"
                      :value="historySourceCodeList[history.judgeId]"
                    />
                  </div>
                  <div class="w-full flex items-center justify-center pb-3">
                    <IconTextButton
                      :border_radius="2"
                      class="mx-1"
                      :font_weight="'700'"
                      :icon="'reviews'"
                      :iconSize="'text-xl'"
                      :padding_bottom="4"
                      :padding_left="12"
                      :padding_right="12"
                      :padding_top="4"
                      :text="$t('message.review')"
                      :textSize="'text-base'"
                      @click="openReviewAlertModal(history.judgeId, history.status)"
                    />
                    <IconTextButton
                      :border_radius="2"
                      class="mx-1"
                      :font_weight="'700'"
                      :icon="'content_copy'"
                      :iconSize="'text-xl'"
                      :padding_bottom="4"
                      :padding_left="12"
                      :padding_right="12"
                      :padding_top="4"
                      :text="$t('message.copy')"
                      :textSize="'text-base'"
                      @click="copyClipboard(historySourceCodeList[history.judgeId])"
                    />
                  </div>
                </div>
              </template>
            </NestedElement>
          </div>
        </div>
      </div>
      <div v-else-if="sortedHistoryList.length <= 0 && !historyListIsLoading" class="p-3">
        {{ $t('message.noData') }}
      </div>
      <div v-if="historyListIsLoading" class="flex justify-center items-center" style="height: 120px">
        <LoadingPanel />
      </div>
      <div v-else-if="historyListError" class="p-3">{{ $t('message.error') }}</div>
    </ButtonPanel>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted } from 'vue'

import HistoryListHeader from './children/HistoryListHeader.vue'

import { JudgeStatusType } from '@/domain/value_objects/judge/JudgeStatusType'
import IconTextButton from '@/presentation/components/Button/IconTextButton.vue'
import NestedElement from '@/presentation/components/Element/NestedElement.vue'
import LoadingPanel from '@/presentation/components/Loading/LoadingPanel.vue'
import MonacoEditorRead from '@/presentation/components/MonacoEditor/MonacoEditorRead.vue'
import ButtonPanel from '@/presentation/components/Panel/ButtonPanel.vue'
import JudgeBadge from '@/presentation/components/Text/JudgeBadge.vue'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useHistoryListPresenter } from '@/presentation/composables/history/useHistoryListPresenter'
import { useHistoryPageStatePresenter } from '@/presentation/composables/history/useHistoryPageStatePresenter'
import { useHistoryPresenter } from '@/presentation/composables/history/useHistoryPresenter'
import { useHistorySourceCodePresenter } from '@/presentation/composables/history/useHistorySourceCodePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'
import { useReviewRequestPresenter } from '@/presentation/composables/reviewRequest/useReviewRequestPresenter'
import timestampToDate from '@/util/date/timestampToDate'

export default defineComponent({
  components: {
    MonacoEditorRead,
    HistoryListHeader,
    ButtonPanel,
    LoadingPanel,
    NestedElement,
    IconTextButton,
    JudgeBadge
  },
  setup(props, ctx) {
    const { problemId } = useProblemStatePresenter()
    const reviewRequestPresenter = useReviewRequestPresenter()
    const modalStatePresenter = useModalStatePresenter()

    const { user } = useAuthStatePresenter()
    const { copyClipboard, programLanguage } = useConfigStatePresenter()
    const { initState, clickSummary } = useHistoryPresenter()
    const {
      historyExpandedList,
      historyLoadingList,
      isFirstOpendPage,
      hasDataInExpandedList,
      hasDataInLoadingList,
      setIsFirstOpendPage
    } = useHistoryPageStatePresenter()
    const {
      historyList,
      hasHistoryInList,
      isLoading: historyListIsLoading,
      error: historyListError
    } = useHistoryListPresenter()
    const {
      historySourceCodeList,
      isLoading: historySourceCodeIsLoading,
      error: historySourceCodeError
    } = useHistorySourceCodePresenter()

    const sortedHistoryList = computed(() => {
      const answerArray = Object.values(historyList.value).sort(
        (a, b) => b.submissionDate.getTime() - a.submissionDate.getTime()
      )
      return answerArray
    })

    const openReviewAlertModal = (judgeId: number, status: JudgeStatusType) => {
      reviewRequestPresenter.setReviewRequestData(judgeId, status)
      modalStatePresenter.setIsShowReviewAlertModal(true)
    }

    const refresh = () => {
      initState(user.value.id, problemId.value)
    }

    if (isFirstOpendPage.value) refresh()

    onMounted(() => {
      setIsFirstOpendPage(false)
    })

    return {
      sortedHistoryList,
      historyList,
      historyListIsLoading,
      historyListError,
      hasHistoryInList,
      historySourceCodeList,
      historySourceCodeIsLoading,
      historySourceCodeError,
      historyLoadingList,
      historyExpandedList,
      hasDataInExpandedList,
      hasDataInLoadingList,
      refresh,
      clickSummary,
      programLanguage,
      copyClipboard,
      timestampToDate,
      openReviewAlertModal
    }
  }
})
</script>

<style lang="postcss" scoped>
.note-box {
  //padding: 0.5em 1em 0.7em 1em;
  margin: 0em 0 1em 0;
}

.top {
  user-select: none;
}
.top-dark {
  user-select: none;
}
.top_in {
  padding: 0.7em 1em 0.7em 1em;
  //color: #5d627b;
}
.click-effect:active {
  @apply bg-gray-600;
}

.loading::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 40vh;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px white solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}
.loading-dark::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 40vh;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px #1e1e1e solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

.loadingCode::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px white solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.loadingCode-dark::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px #3e3e3e solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.hover-copy {
  text-decoration: none;

  @apply relative inline-block;

  &::before {
    content: '';
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #232a3b;
    @apply block absolute fadein;
  }

  &::after {
    font-family: sans-serif;
    content: attr(data-tooltip);
    bottom: 165%;
    left: 50%;
    transform: translateX(-50%);
    @apply bg-aoj text-white rounded px-2 py-1 block absolute font-bold text-sm pointer-events-none z-10 fadein;
  }
}

.hover-copy-dark {
  text-decoration: none;

  @apply relative inline-block;

  &::before {
    content: '';
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
    @apply block absolute fadein;
  }

  &::after {
    font-family: sans-serif;
    content: attr(data-tooltip);
    bottom: 165%;
    left: 50%;
    transform: translateX(-50%);
    @apply bg-black border text-dark-text rounded px-2 py-1 block absolute font-bold text-sm pointer-events-none z-10 fadein;
  }
}

.fadein {
  opacity: 1;
  animation-name: fadeout;
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-delay: 1s;
}

@keyframes fadeout {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.select {
  user-select: none;
}
.click-effect:active {
  @apply border-gray-900 bg-gray-200;
}

.click-effect-dark:active {
  @apply bg-dark-10;
}
</style>
